import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { getToken, useTheme } from '../../../utils/themeTokens';
import {
  colorTokenForClass,
  formatConfidence,
  overlayStrokeWidth,
  overlayViewBox,
  polygonPoints,
} from '../utils/predictionView';

/**
 * The image with its boxes drawn over it.
 *
 * WHY AN SVG WITH THE IMAGE'S OWN viewBox, AND NOT A CANVAS OR SCALED RECTS.
 * The boxes arrive in pixels of the ORIGINAL image. Giving the SVG a viewBox of
 * exactly that size and stretching it over the rendered `<img>` makes the
 * browser do the scaling, so the overlay tracks the image through every window
 * resize and column width with no measurement and no redraw. The only thing
 * that must be corrected by hand is the STROKE, which the same scaling would
 * otherwise turn into a hairline on a 4000px photo — the same arithmetic the
 * labeling canvas does against its zoom.
 *
 * Colours come from `getToken`, never hardcoded: this screen has to be right in
 * both themes and the palette lives in `src/styles/theme.css`.
 */
const PredictionCanvas = ({ image, result, detections, highlighted, onHighlight }) => {
  const theme = useTheme();
  const wrapRef = useRef(null);
  const [renderedWidth, setRenderedWidth] = useState(0);

  // The rendered width feeds the stroke correction only. A ResizeObserver
  // rather than a window listener because the column can change width without
  // the window doing so — the panel is flex, and the results column grows with
  // its content.
  useLayoutEffect(() => {
    const node = wrapRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setRenderedWidth(node.clientWidth));
    observer.observe(node);
    setRenderedWidth(node.clientWidth);
    return () => observer.disconnect();
  }, [image?.id]);

  const colorFor = useCallback(
    (classId) => getToken(colorTokenForClass(classId), '#10b981'),
    // `theme` is not read in the body — it is the dependency that makes this
    // recompute when the user flips the toggle, because getToken reads the
    // computed value off <html> and that changes with the class.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  );

  const box = overlayViewBox(result?.image);
  const stroke = overlayStrokeWidth(result?.image, renderedWidth);

  return (
    <div className="lf-predict-canvas" ref={wrapRef}>
      <img src={image.previewUrl} alt={image.name} className="lf-predict-canvas__img" />
      {box && detections.length > 0 && (
        <svg
          className="lf-predict-canvas__overlay"
          viewBox={box.viewBox}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {detections.map((detection, index) => {
            if (!Array.isArray(detection.bbox) || detection.bbox.length < 4) return null;
            const [x1, y1, x2, y2] = detection.bbox;
            const color = colorFor(detection.class_id);
            const isDim = highlighted !== null && highlighted !== undefined && highlighted !== index;
            const key = `${detection.class_id}-${index}`;
            return (
              <g
                key={key}
                opacity={isDim ? 0.25 : 1}
                onMouseEnter={() => onHighlight(index)}
                onMouseLeave={() => onHighlight(null)}
                style={{ pointerEvents: 'auto' }}
              >
                {Array.isArray(detection.polygon) && detection.polygon.length > 2 ? (
                  <polygon
                    points={polygonPoints(detection.polygon)}
                    fill={color}
                    fillOpacity={0.22}
                    stroke={color}
                    strokeWidth={stroke}
                  />
                ) : (
                  <rect
                    x={x1}
                    y={y1}
                    width={Math.max(x2 - x1, 0)}
                    height={Math.max(y2 - y1, 0)}
                    fill="none"
                    stroke={color}
                    strokeWidth={stroke}
                  />
                )}
                {/* The label sits INSIDE the top edge rather than above it, so a
                    detection touching the top of the frame is not clipped off
                    the image entirely. */}
                <text
                  x={x1 + stroke * 2}
                  y={y1 + stroke * 8}
                  fill={color}
                  fontSize={stroke * 8}
                  style={{ paintOrder: 'stroke', userSelect: 'none' }}
                  stroke={getToken('--background', '#000')}
                  strokeWidth={stroke * 1.5}
                >
                  {detection.class_name} {formatConfidence(detection.confidence)}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
};

export default PredictionCanvas;
